import { Tooltip } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { checkDeviceToken, CheckDeviceTokenContracts } from '../Async/CheckDeviceTokenAsync';
import { ButtonMode } from '../Components/Button';
import { StyledDialog } from '../Components/Dialog/StyledDialog';
import { StyledDialogButton } from '../Components/Dialog/StyledDialogButton';
import { StyledDialogContent } from '../Components/Dialog/StyledDialogContent';
import { useError } from '../Components/ErrorBoundary';
import { DangerIcon, TestTokenIcon, TickSolidIcon } from '../Components/Icons';
import { LayoutCentral } from '../Components/Layout/LayoutCentral';
import { LoadingPlaceholder } from '../Components/Placeholder';
import { Nullable } from '../Shared/Types';
import { successGlowBorder, errorText as errorTextColor } from '../Styles/Colours';
import styles from './DeviceTokenTester.module.scss';

interface DeviceTokenTesterProps {
    authoringSystem: string;
    authoringSystemForeignKey: string;
}

interface DeviceTokenDialogProps extends DeviceTokenTesterProps {
    onClose: () => void;
}

const DialogContent: React.FC<{ title: string; icon: React.ReactElement }> = props => (
    <div>
        <div className={styles.mainIcon}>{props.icon}</div>
        <div>
            <h5 className={styles.heading}>{props.title}</h5>
        </div>
        <div className={styles.message}>
            <span className={styles.messageSpan}>{props.children}</span>
        </div>
    </div>
);

function DeviceTokenTestDialog(props: DeviceTokenDialogProps): JSX.Element {
    function getErrorText(resp: CheckDeviceTokenContracts.Response): string {
        if (!resp.error) {
            return 'unknown error';
        } else if (!resp.error.code) {
            return resp.error.message || 'unknown error';
        }
        return resp.error.code + ': ' + resp.error.message || 'unknown error';
    }

    const [loading, setLoading] = useState<boolean>(false);
    const [errorText, setErrorText] = useState<Nullable<string>>(null);
    const [messageText, setMessageText] = useState<Nullable<string>>(null);
    const throwError = useError();

    async function testToken() {
        try {
            setErrorText(null);
            setLoading(true);
            setMessageText(null);
            const resp = await checkDeviceToken(props.authoringSystem, props.authoringSystemForeignKey);
            if (resp.success) {
                setLoading(false);
                setMessageText('Device connected to Medicare successfully.');
            } else {
                setLoading(false);
                setErrorText(getErrorText(resp));
                setMessageText('Device failed to connect to Medicare.');
            }
        } catch (err) {
            throwError(err);
        }
    }

    useEffect(() => {
        testToken();
    }, []);

    return (
        <StyledDialog
            size='xs'
            open
            title='Device test'
            actions={[
                <StyledDialogButton
                    title='Close'
                    key='close-device-token-dialog-props'
                    onClick={props.onClose}
                    mode={ButtonMode.border}
                    width={112}
                    testId='deviceTokenTester-close-dialog'
                >
                    Close
                </StyledDialogButton>
            ]}
            contentComponent={
                <StyledDialogContent>
                    <LayoutCentral>
                        {loading ? (
                            <LoadingPlaceholder />
                        ) : (
                            <DialogContent
                                icon={
                                    errorText ? (
                                        <DangerIcon size='64' noPadding color={errorTextColor} />
                                    ) : (
                                        <TickSolidIcon size='64' noPadding color={successGlowBorder} />
                                    )
                                }
                                title={messageText || ''}
                            >
                                {errorText && <span data-test-id='deviceTokenTester-error-message'>{errorText}</span>}
                            </DialogContent>
                        )}
                    </LayoutCentral>
                </StyledDialogContent>
            }
        />
    );
}

export function DeviceTokenTester(props: DeviceTokenTesterProps): JSX.Element {
    const [showDialog, setShowDialog] = useState<boolean>(false);

    function iconClick(e: React.MouseEvent<HTMLElement>) {
        e.stopPropagation();
        setShowDialog(true);
    }

    function dialogClose() {
        setShowDialog(false);
    }

    return (
        <>
            <Tooltip title='Test device'>
                <span>
                    <TestTokenIcon size='40' className={styles.icon} onClick={iconClick} />
                </span>
            </Tooltip>
            {showDialog && (
                <DeviceTokenTestDialog
                    authoringSystem={props.authoringSystem}
                    onClose={dialogClose}
                    authoringSystemForeignKey={props.authoringSystemForeignKey}
                />
            )}
        </>
    );
}
